'use client';

import { useMemo } from 'react';
import { useEntitlements, useEntitlementClasses } from './hooks';
import {
  resolveSpecificGiftBeneficiariesByItem,
  type ItemBeneficiaryShare
} from './specific-gift-defaults';

/**
 * **Who the will gifts an asset to, and in what share, for a surface about to
 * default a beneficiary selection from it.**
 *
 * Every instruction that transfers an asset to beneficiaries should open
 * prefilled from the matter's specific-gift entitlements, and there are now six
 * surfaces that ask the question: the inventory attribute record, the shares
 * forms, and the redemption Set-instructions sheets for shares, property,
 * vehicles and other assets. The resolution itself is a faithful port of the
 * backend's canonical resolver (`specific-gift-defaults.ts`), but the *plumbing*
 * around it — which queries to read, and the one non-obvious rule below about
 * when the answer is trustworthy — had been restated at each host, and was
 * simply absent at four of them (VALE-1431).
 *
 * ## `seedLoading` is the load-bearing part
 *
 * **An unloaded fetch is indistinguishable from a genuine absence**, which is
 * the whole of VALE-817. `sharesFor` returns `undefined` both while the
 * entitlements are in flight and when the item has no specific gift, and an
 * unloaded contact roster drops every share in the consumer's own beneficiary
 * filter. A seed that runs against either produces an empty panel, and the
 * transition it keys off has already been spent — so the lawyer re-enters every
 * beneficiary by hand with nothing on screen saying the automation no-opped.
 *
 * So all three fetches count, and a consumer must hold its seed until
 * `seedLoading` is false rather than run it against nothing. Contacts are
 * fetched by the host (which needs them for its own picker anyway), so their
 * loading state is passed in rather than fetched again here.
 *
 * A disabled query reports `isLoading: false`, so `seedLoading` is false for
 * every surface that passes `enabled: false`.
 */
export function useEntitlementSeed({
  matterId,
  enabled = true,
  contactsLoading = false
}: {
  matterId: number | undefined;
  /** Pass false for a surface that can carry no gift — skips both fetches. */
  enabled?: boolean;
  /** The host's own contact-roster loading state. */
  contactsLoading?: boolean;
}): {
  /** Resolved shares for one item, or `undefined` when it carries no gift. */
  sharesFor: (
    itemId: number | null | undefined
  ) => ItemBeneficiaryShare[] | undefined;
  seedLoading: boolean;
} {
  // Both hooks self-gate on a truthy matterId, so 0 disables the query.
  const queryMatterId = enabled && matterId ? matterId : 0;

  const { data: entitlements, isLoading: loadingEntitlements } =
    useEntitlements(queryMatterId);
  const { data: classes, isLoading: loadingClasses } =
    useEntitlementClasses(queryMatterId);

  const sharesByItem = useMemo(() => {
    if (!queryMatterId || !entitlements) {
      return new Map<number, ItemBeneficiaryShare[]>();
    }
    const classesById = new Map((classes ?? []).map((c) => [c.id, c]));
    return resolveSpecificGiftBeneficiariesByItem(entitlements, classesById);
  }, [queryMatterId, entitlements, classes]);

  const sharesFor = useMemo(
    () => (itemId: number | null | undefined) =>
      itemId == null ? undefined : sharesByItem.get(itemId),
    [sharesByItem]
  );

  return {
    sharesFor,
    seedLoading: loadingEntitlements || loadingClasses || contactsLoading
  };
}
